import i18next from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

import ar from './ar.json';
import en from './en.json';

export type Locale = 'en' | 'ar';

export const LOCALE_STORAGE_KEY = 'app.locale';

export function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * `fallbackLng: 'en'` IS NFR-01c / GEN-180b: "a label that is missing in the
 * reader's language must fall back to the other language, never to an empty
 * space." GEN-180 and GEN-180a (every label and every menu entry in both
 * languages) and GEN-180c (checked as part of the build) are the key-parity
 * test in i18n-resources.test.ts.
 * `returnEmptyString: false` is the same rule's other half — a key that exists
 * but is blank falls back too, rather than rendering nothing, which is the case
 * the rule actually names ("an empty menu entry looks like a broken product").
 * NFR-01b — the two dictionaries did not match, and the rule asks for "an
 * automatic check that fails when they disagree" — is `i18n-resources.test.ts`.
 *
 * NFR-01 IS CLOSED (14 Aug 2026). The rule is "no text may be written into a
 * screen", and its four named survivors — the letter `h` after an hour count
 * (`timesheets-page`, `billing-page`, `tasks-page`, `planning-page`), the
 * letter `d` after a day count (`inventory-report-page`), the per-cent sign
 * (a dozen screens) and the ▲/▼ glyphs in `ChangePill` — are dictionary text
 * now. The mechanism is `<Hours>`/`<Days>`/`<Pct>` in `components/amount.tsx`
 * (the `<Qty>` precedent: value and unit composed ONCE, in one
 * `<bdi dir="ltr">` island, unit text from the `unit.*` keys), with
 * `t('unit.hours'|'unit.days'|'unit.percent', { value })` inside an existing
 * single island where a RUN of figures shares one (`12h / 40h` — separate
 * islands would let RTL reorder the separators), `unit.percentSign` for the
 * bare sign beside an input, and `unit.up`/`unit.down` for the pill glyphs.
 * `unit-text.test.tsx` pins the mechanism; the Arabic page assertions live in
 * `projects-screens` (س) and `reports-screens` (ي). EC-19-45's full list is
 * closed with them: the arrow and return glyphs became the direction-aware
 * `unit.arrow`/`unit.corrects` (NFR-04), and `Toggle theme` is
 * `shell.themeToggle` dictionary text (EC-19-55). What deliberately stays
 * out: `%` inside sentences the spec quotes verbatim (the sentence is the
 * unit of translation, ERR-PUR-06-05's lesson) and XLSX/CSV export cells
 * (exports carry numbers, not screen text).
 *
 * NFR-08 — "built so a third language can be added without changing any
 * screen" — is the `resources` map below plus the rule NFR-01 enforces
 * everywhere: no screen owns a string, every screen calls `t()`. A third
 * language is a third entry in this map (and its row in the switch); no screen
 * file changes.
 *
 * i18next + i18next-icu, flat keys (DECISION.md §2.10 i18n row). The keys
 * contain literal dots — "auth.signIn" is one key, not a nested path — which
 * is why keySeparator and nsSeparator are switched off. ICU is used only where
 * it earns its keep (arguments like {brand}, counted strings later); plain
 * strings stay plain.
 *
 * Both dictionaries ship in the main bundle for now: at ~100 keys per locale
 * the lazy-loading machinery would cost more than it saves. Revisit when the
 * dictionaries approach the prototype's 3,600 keys.
 */
export const i18n = i18next.createInstance();

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    lng: readStoredLocale(),
    fallbackLng: 'en',
    keySeparator: false,
    nsSeparator: false,
    // Resources are inline, so init can complete synchronously — which also
    // lets the test suite render without awaiting an init event.
    initAsync: false,
    interpolation: {
      // React already escapes; double-escaping shows &amp; in labels.
      escapeValue: false,
    },
    returnEmptyString: false,
  });

export default i18n;
